import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { AssetEditor } from "./AssetEditor";
import { fetchApi } from "@/lib/api";
import { Asset, AssetSchema } from "@forgepoint/shared";
import { z } from "zod";

const GetAssetsByIdsResponseSchema = z.object({
  assets: z.array(AssetSchema),
});

interface ValidateDesignFilesPreviewControllerProps {
  assetIds: string[];
  question?: string | null;
}

export function ValidateDesignFilesPreviewController({
  assetIds,
  question,
}: ValidateDesignFilesPreviewControllerProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAssetIndex, setSelectedAssetIndex] = useState(0);

  useEffect(() => {
    const loadAssets = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchApi(
          `api/assets?ids=${assetIds.join(",")}`,
          "GET",
        );
        if (!response.ok) {
          throw new Error("Failed to fetch assets");
        }
        const data = GetAssetsByIdsResponseSchema.parse(response.data);
        setAssets(data.assets);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load assets");
      } finally {
        setLoading(false);
      }
    };

    if (assetIds.length > 0) {
      loadAssets();
    }
  }, [assetIds]);

  const selectedAsset = assets[selectedAssetIndex];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No assets to display</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {assets.length > 1 && (
        <div className="border-b px-4 py-3 bg-muted/20">
          <div className="flex gap-2 overflow-x-auto">
            {assets.map((asset, index) => (
              <button
                key={asset.id}
                onClick={() => setSelectedAssetIndex(index)}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  index === selectedAssetIndex
                    ? "bg-primary text-primary-foreground"
                    : "bg-background hover:bg-muted"
                }`}
              >
                {asset.fileName}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex-1 overflow-hidden">
        {selectedAsset && (
          <AssetEditor
            asset={selectedAsset}
            question={question}
            onUpdate={(metadata) => {
              setAssets((prevAssets) =>
                prevAssets.map((a, i) =>
                  i === selectedAssetIndex ? { ...a, metadata } : a,
                ),
              );
            }}
          />
        )}
      </div>
    </div>
  );
}
